/**
 * Scroll reveal: content fades and rises into place the first time it
 * scrolls into view.
 *
 * Two attributes mark what moves:
 * - [data-reveal] on an element reveals that element.
 * - [data-reveal-stagger] on a container reveals each of its child elements.
 * Either takes an optional variant, styled in snusify-reveal.css: "soft"
 * (fade and settle, for rails that scroll sideways), "fade", "line" (a rule
 * drawn across), or "media" (the frame fades while its image settles from a
 * slight zoom).
 *
 * Whatever comes into view together plays as one wave in page order, a
 * step apart, so a row of cards staggers while the next row waits its turn.
 *
 * The layout's inline script puts .snusify-reveal on the root before first
 * paint, and only when motion is welcome; without that class nothing is
 * hidden. Content added later, a tab's cards or a brand's rail, is picked up
 * as it arrives.
 */

/** Delay between items in one wave. */
const STEP_MS = 80;
/** After this many steps the rest of a wave comes in together. */
const MAX_STEPS = 8;

const ITEM_SELECTOR = '[data-reveal], [data-reveal-stagger] > *';
const SKIP_TAGS = new Set(['TEMPLATE', 'SCRIPT', 'STYLE', 'LINK']);

const root = document.documentElement;

/** @type {Set<Element>} */
const pending = new Set();

const observer = new IntersectionObserver(onIntersect, { threshold: 0.12 });

window.snusifyReveal = true;

if (root.classList.contains('snusify-reveal')) {
  scan(document.body);
  new MutationObserver(onMutate).observe(document.body, { childList: true, subtree: true });
  document.addEventListener('focusin', onFocus);
}

/**
 * Starts watching one element, unless it has been revealed already.
 * @param {Element} el
 */
function watch(el) {
  if (SKIP_TAGS.has(el.tagName) || pending.has(el) || el.classList.contains('is-revealed')) return;
  pending.add(el);
  observer.observe(el);
}

/**
 * Watches every item in and under a node.
 * @param {Element} node
 */
function scan(node) {
  if (node.matches(ITEM_SELECTOR)) watch(node);
  for (const el of node.querySelectorAll(ITEM_SELECTOR)) watch(el);
}

/** @param {MutationRecord[]} records */
function onMutate(records) {
  for (const record of records) {
    for (const node of record.addedNodes) {
      if (node instanceof Element) scan(node);
    }
  }
}

/** @param {IntersectionObserverEntry[]} entries */
function onIntersect(entries) {
  const wave = new Set();

  for (const entry of entries) {
    if (entry.isIntersecting) {
      wave.add(entry.target);
      continue;
    }
    // Already scrolled past before it was seen, after a restored scroll
    // position or a jump to an anchor: show it as it is.
    const box = entry.boundingClientRect;
    if (box.height > 0 && box.bottom <= 0) show(entry.target);
  }

  if (wave.size === 0) return;

  // A rail cut off at the side never reports its hidden cards as visible,
  // so the ones on the same row come along with those that are.
  for (const el of [...wave]) {
    const parent = el.parentElement;
    if (!parent?.hasAttribute('data-reveal-stagger')) continue;
    for (const sibling of parent.children) {
      if (pending.has(sibling) && inViewVertically(sibling)) wave.add(sibling);
    }
  }

  const ordered = [...wave].sort((a, b) =>
    a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1
  );

  let step = 0;
  for (const el of ordered) {
    play(el, Math.min(step, MAX_STEPS) * STEP_MS);
    step += 1;
  }
}

/**
 * Brings keyboard focus into view at once: a hidden element must never hold
 * focus.
 * @param {FocusEvent} event
 */
function onFocus(event) {
  let el = event.target instanceof Element ? event.target : null;
  while (el) {
    if (pending.has(el)) show(el);
    el = el.parentElement;
  }
}

/**
 * @param {Element} el
 * @returns {boolean}
 */
function inViewVertically(el) {
  const box = el.getBoundingClientRect();
  return box.height > 0 && box.top < window.innerHeight && box.bottom > 0;
}

/**
 * Plays the reveal after a delay, then clears it so the element's own
 * styles and animations take over again.
 * @param {Element} el
 * @param {number} delay
 */
function play(el, delay) {
  release(el);
  if (!(el instanceof HTMLElement)) {
    el.classList.add('is-revealed', 'is-reveal-done');
    return;
  }

  el.style.setProperty('--snusify-reveal-delay', `${delay}ms`);

  /** @param {AnimationEvent} event */
  const finish = (event) => {
    if (event.target !== el) return;
    el.removeEventListener('animationend', finish);
    el.removeEventListener('animationcancel', finish);
    el.classList.add('is-reveal-done');
    el.style.removeProperty('--snusify-reveal-delay');
  };
  el.addEventListener('animationend', finish);
  el.addEventListener('animationcancel', finish);
  el.classList.add('is-revealed');
}

/**
 * Shows an element straight away, without the animation.
 * @param {Element} el
 */
function show(el) {
  release(el);
  el.classList.add('is-revealed', 'is-reveal-done');
}

/** @param {Element} el */
function release(el) {
  pending.delete(el);
  observer.unobserve(el);
}
